/**
 * Parental gate pass, valid "as long as you stay in /parents".
 *
 * The home shows the gate and, once passed, marks the pass BEFORE navigating
 * to /parents; the route also marks it if passed there (direct access by
 * URL, Families Policy). The pass is NOT consumed on mount: a language change
 * re-mounts the whole tree (app.tsx unmounts the router while it loads the
 * catalog) and the gate must not be asked again in that case. It is invalidated when
 * navigating outside /parents (see GuardedParentsPage) and, being module state,
 * also on any reload: the gate is asked on every visit.
 */

let gatePass = false;
/** Epoch ms at which the pass expires. `0` = no expiry of its own (historical behavior). */
let expiresAt = 0;

/**
 * Marks the gate as passed for the current visit to /parents.
 *
 * `ttlMinutes` (configurable by the adult, see `app-config`) keeps the pass
 * alive for that long even if you navigate away: an adult who goes in to adjust three
 * things and leaves to the home should not solve the sum three times. With `0` —the
 * default— the pass dies on exit, which is how it behaved until now.
 */
export function markGatePassed(ttlMinutes = 0): void {
  gatePass = true;
  expiresAt = ttlMinutes > 0 ? Date.now() + ttlMinutes * 60_000 : 0;
}

/** Is this visit's pass still valid? */
export function hasGatePass(): boolean {
  if (!gatePass) {
    return false;
  }
  if (expiresAt > 0 && Date.now() >= expiresAt) {
    resetGatePass();
    return false;
  }
  return gatePass;
}

/** Invalidates the pass (on leaving /parents, or on expiry). */
export function resetGatePass(): void {
  gatePass = false;
  expiresAt = 0;
}

/** Is there a timed pass that survives leaving /parents? */
export function gatePassSurvivesNavigation(): boolean {
  return hasGatePass() && expiresAt > 0;
}
